import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';
import { DATE_FORMAT_OPTIONS, DATETIME_FORMAT_OPTIONS } from '../../config/templateVariables';

// Мемоизированное расширение для подсветки переменных
const pluginKey = new PluginKey('smartVariableHighlight');

// Экспортируем ключ/хелпер, чтобы другие части редактора (например, ресайз IF-блоков)
// могли корректно "прилипать" к границам {{...}} и не разрывать переменные.
export const smartVariableHighlightPluginKey = pluginKey;

export const findVariableDecorationAtPos = (state, pos) => {
  if (!state || typeof pos !== 'number') return null;
  const decorationSet = pluginKey.getState(state);
  if (!decorationSet || typeof decorationSet.find !== 'function') return null;
  // Ищем декорации в небольшой окрестности: так мы надёжно поймаем попадания в границы.
  const found = decorationSet.find(Math.max(0, pos - 1), pos + 1) || [];
  const hit = found.find((d) => pos > d.from && pos < d.to);
  if (!hit) return null;
  return { from: hit.from, to: hit.to };
};

const SmartVariableExtension = Extension.create({
  name: 'smartVariableHighlight',

  addOptions() {
    return {
      availableVariables: {}
    };
  },

  addProseMirrorPlugins() {
    const { availableVariables } = this.options;
    return [
      new Plugin({
        key: pluginKey,
        state: {
          init(_, { doc }) {
            return findVariables(doc, availableVariables);
          },
          apply(transaction, oldState) {
            const forceUpdate = transaction.getMeta('forceUpdateVariables') === true;
            if (!transaction.docChanged && !forceUpdate) {
              return oldState.map(transaction.mapping, transaction.doc);
            }
            return findVariables(transaction.doc, availableVariables);
          }
        },
        props: {
          decorations(state) {
            return pluginKey.getState(state);
          },
          handleClick(view, pos) {
            const pluginState = pluginKey.getState(view.state);
            if (!pluginState) return false;
            const decorations = pluginState.find();
            // ВАЖНО: клик "сразу после" переменной (pos === dec.to) должен ставить курсор ПОСЛЕ неё.
            // Если считать это попаданием внутрь декорации, то ProseMirror не получает фокус корректно
            // и каретка визуально может "пропасть".
            const targetDecoration = decorations.find(dec => pos > dec.from && pos < dec.to);
            if (targetDecoration) {
              const midpoint = targetDecoration.from + Math.floor((targetDecoration.to - targetDecoration.from) / 2);
              const direction = pos <= midpoint ? 'left' : 'right';
              enforceCursorOutsideVariables(view, view.state, null, direction, targetDecoration);
              try { view.focus(); } catch (_) { /* no-op */ }
              return true;
            }
            return false;
          },
          handleChange(view, prevState) {
            return enforceCursorOutsideVariables(view, view.state, prevState);
          },
          handleDOMEvents: {
            mouseup: (view) => {
              // ВАЖНО: не "глушим" событие mouseup.
              // Ранее возврат true мог мешать ProseMirror синхронизировать DOM‑selection,
              // из‑за чего каретка после переменной визуально пропадала.
              enforceCursorOutsideVariables(view, view.state);
              return false;
            }
          },
          handleKeyDown(view, event) {
            const pluginState = pluginKey.getState(view.state);
            if (!pluginState) {
              return false;
            }

            const { state } = view;
            const { selection } = state;
            const { from, to, empty, head } = selection;
            const decorations = pluginState.find();

            const findDecoration = (predicate) => decorations.find(predicate);

            const handleArrow = (direction) => {
              const step = direction === 'right' ? 1 : -1;
              const nextPos = head + step;
              const targetDecoration = decorations.find(dec => {
                if (direction === 'right') {
                  return nextPos > dec.from && nextPos < dec.to;
                }
                return nextPos >= dec.from && nextPos < dec.to;
              });

              if (targetDecoration) {
                event.preventDefault();
                event.stopPropagation();
                const targetPos = direction === 'right' ? targetDecoration.to : targetDecoration.from;
                const bias = direction === 'right' ? 1 : -1;
                const resolved = state.doc.resolve(targetPos);
                const textSelection = TextSelection.near(resolved, bias);
                view.dispatch(state.tr.setSelection(textSelection));
                return true;
              }
              return false;
            };

            if (event.key === 'Backspace' || event.key === 'Delete') {
              const targetDecoration = findDecoration((decoration) => {
                const start = decoration.from;
                const end = decoration.to;

                if (!empty) {
                  return from >= start && to <= end;
                }

                if (event.key === 'Backspace') {
                  return from > start && from <= end;
                }

                return from >= start && from < end;
              });

              if (targetDecoration) {
                event.preventDefault();
                const tr = state.tr.delete(targetDecoration.from, targetDecoration.to);
                view.dispatch(tr);
                return true;
              }
            }

            if (event.key === 'ArrowLeft') {
              if (handleArrow('left')) {
                return true;
              }
            }

            if (event.key === 'ArrowRight') {
              if (handleArrow('right')) {
                return true;
              }
            }

            if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
              const direction = event.key === 'ArrowLeft' ? 'left' : 'right';
              setTimeout(() => {
                enforceCursorOutsideVariables(view, view.state, state, direction);
              }, 0);
              return false;
            }

            if (event.key === ' ' || event.key === 'Enter' || event.key === '}') {
              setTimeout(() => {
                const { state: currentState, dispatch } = view;
                dispatch(currentState.tr.setMeta('forceUpdateVariables', true));
              }, 50);
            }
            return false;
          }
        }
      })
    ];
  }
});

// Функция для поиска переменных в документе
function findVariables(doc, availableVariables = {}) {
  const decorations = [];
  const variableRegex = /\{\{[^}]*\}\}/g; // Изменено: [^}]* вместо [^}]+ чтобы ловить незавершенные переменные
  const formatOptions = [...DATE_FORMAT_OPTIONS, ...DATETIME_FORMAT_OPTIONS];
  const declensionSuffixes = {
    _r: 'род. падеж - кого? чего?',
    _d: 'дат. падеж - кому? чему?',
    _v: 'вин. падеж - кого? что?',
    _t: 'тв. падеж - кем? чем?',
    _p: 'пр. падеж - о ком? о чём?'
  };
  const itemFieldLabels = {
    lot_number: 'Номер лота',
    lot_numbers: 'Номера лотов',
    lot_count: 'Количество лотов',
    creditor_name: 'Наименование кредитора',
    creditor_names: 'Наименования кредиторов'
  };

  const resolveVariableDescription = (name) => {
    const normalized = String(name || '').replace(/\s+/g, '_');
    const direct = availableVariables[name] || availableVariables[normalized];
    if (direct) {
      return direct;
    }

    for (const [suffix, label] of Object.entries(declensionSuffixes)) {
      if (!name || !name.endsWith(suffix)) continue;
      const baseName = name.slice(0, -suffix.length);
      const baseNormalized = String(baseName || '').replace(/\s+/g, '_');
      const baseLabel = availableVariables[baseName] || availableVariables[baseNormalized];
      if (baseLabel) {
        return `${baseLabel} (${label})`;
      }
    }

    for (const opt of formatOptions) {
      if (!opt?.suffix) continue;
      if (!name || !name.endsWith(opt.suffix)) continue;
      const baseName = name.slice(0, -opt.suffix.length);
      const baseNormalized = String(baseName || '').replace(/\s+/g, '_');
      const baseLabel = availableVariables[baseName] || availableVariables[baseNormalized];
      if (baseLabel) {
        const hint = opt.description || opt.label;
        return hint ? `${baseLabel} (${hint})` : baseLabel;
      }
    }

    if (name && name.startsWith('item.')) {
      const field = name.slice(5);
      if (itemFieldLabels[field]) {
        return itemFieldLabels[field];
      }
    }

    return null;
  };

  doc.descendants((node, pos) => {
    if (node.isText && node.text) {
      let match;
      const text = node.text;
      variableRegex.lastIndex = 0;
      
      while ((match = variableRegex.exec(text)) !== null) {
        const from = pos + match.index;
        const to = from + match[0].length;
        const rawName = match[0].slice(2, -2);
        const variableName = rawName.trim();
        const [baseVariableName, ...mods] = variableName.split('|').map((s) => s.trim()).filter(Boolean);
        const normalizedName = String(baseVariableName || variableName).replace(/\s+/g, '_');
        const baseDescription = resolveVariableDescription(baseVariableName || normalizedName)
          || resolveVariableDescription(variableName)
          || `Переменная ${baseVariableName || variableName}`;
        const description = mods.length
          ? `${baseDescription} (модификатор: ${mods.join('|')})`
          : baseDescription;

        // Проверяем, что это корректная переменная (содержит хотя бы один символ между {{}})
        const variableContent = match[0];
        const isComplete = variableContent.length > 4 && variableContent.startsWith('{{') && variableContent.endsWith('}}');
        const hasContent = variableContent.replace(/[{}]/g, '').trim().length > 0;
        
        if (isComplete && hasContent) {
          decorations.push(
            Decoration.inline(from, to, {
              class: 'template-variable-highlight',
              title: description,
              'data-variable-name': variableName,
              'data-variable-description': description
            })
          );
        }
      }
    }
  });

  const result = DecorationSet.create(doc, decorations);
  return result;
}

function enforceCursorOutsideVariables(view, state = view.state, prevState = null, direction, targetDecoration) {
  const pluginState = pluginKey.getState(state);
  if (!pluginState) return false;

  const { selection } = state;
  const decorations = pluginState.find();

  let decoration = targetDecoration;
  if (!decoration) {
    decoration = decorations.find(dec => selection.from > dec.from && selection.from < dec.to);
  }

  if (decoration) {
    let moveDirection = direction;
    if (!moveDirection && prevState) {
      const prevPos = prevState.selection.head;
      const currentPos = selection.head;
      if (currentPos !== prevPos) {
        moveDirection = currentPos > prevPos ? 'right' : 'left';
      }
    }
    if (!moveDirection) {
      moveDirection = 'right';
    }

    const targetPos = moveDirection === 'left' ? decoration.from : decoration.to;
    const bias = moveDirection === 'left' ? -1 : 1;
    const resolved = state.doc.resolve(targetPos);
    const textSelection = TextSelection.near(resolved, bias);
    const tr = state.tr.setSelection(textSelection);
    view.dispatch(tr);
    try { view.focus(); } catch (_) { /* no-op */ }
    return true;
  }

  return false;
}

export default SmartVariableExtension;
